// VTM 图表配置构建器：统一 ECharts 选项，消除各统计页近乎重复的选项代码。
// 全部默认使用品牌调色板与透明背景，随亮/暗主题自然融入。

export const CHART_COLORS = ["#01E1A8", "#1E6FB8", "#E5A04C", "#F06980", "#5AA7E8", "#12805C", "#102A43"];

// 读取当前主题的文本/网格色（暗色模式下自动切换）
const themeColors = () => {
  const dark = document.documentElement.getAttribute("data-theme") === "dark";
  return {
    axis: dark ? "#94a9bd" : "#6f7686",
    split: dark ? "rgba(255,255,255,0.08)" : "rgba(16,42,67,0.08)",
    label: dark ? "#dbe4ee" : "#1b2b3a"
  };
};

const baseGrid = { left: 12, right: 16, top: 28, bottom: 8, containLabel: true };

const baseTooltip = extra => ({
  trigger: "axis",
  confine: true,
  backgroundColor: "rgba(16,42,67,0.92)",
  borderWidth: 0,
  textStyle: { color: "#fff", fontSize: 12 },
  ...extra
});

// 环形占比图
export const pieOption = ({ data, name = "", radius = ["42%", "68%"] } = {}) => {
  const t = themeColors();
  return {
    color: CHART_COLORS,
    tooltip: { trigger: "item", confine: true, formatter: "{b}: {c} ({d}%)" },
    legend: { type: "scroll", bottom: 0, textStyle: { color: t.axis, fontSize: 11 } },
    series: [
      {
        name,
        type: "pie",
        radius,
        center: ["50%", "45%"],
        avoidLabelOverlap: true,
        itemStyle: { borderColor: "transparent", borderWidth: 2 },
        label: { show: false },
        emphasis: { label: { show: true, fontSize: 13, fontWeight: 600, color: t.label } },
        data: (data || []).map(d => ({ name: d.name, value: Math.abs(Number(d.value) || 0) }))
      }
    ]
  };
};

// 趋势折线/面积图（支持单序列或多序列）
// series: [{ name, data:[] }]，xData: string[]
export const trendLineOption = ({ xData = [], series = [], area = false } = {}) => {
  const t = themeColors();
  const multi = series.length > 1;
  return {
    color: CHART_COLORS,
    backgroundColor: "transparent",
    tooltip: baseTooltip(),
    legend: multi ? { type: "scroll", top: 0, textStyle: { color: t.axis, fontSize: 11 } } : undefined,
    grid: { ...baseGrid, top: multi ? 34 : 20 },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: xData,
      axisLine: { lineStyle: { color: t.split } },
      axisLabel: { color: t.axis, fontSize: 11 },
      axisTick: { show: false }
    },
    yAxis: {
      type: "value",
      splitLine: { lineStyle: { color: t.split } },
      axisLabel: { color: t.axis, fontSize: 11 }
    },
    series: series.map((s, i) => ({
      name: s.name,
      type: "line",
      smooth: true,
      symbol: "circle",
      symbolSize: 6,
      showSymbol: !multi,
      lineStyle: { width: 2.5 },
      areaStyle:
        area && !multi
          ? {
              opacity: 0.18,
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: CHART_COLORS[i % CHART_COLORS.length] },
                  { offset: 1, color: "rgba(1,225,168,0)" }
                ]
              }
            }
          : undefined,
      data: s.data
    }))
  };
};

// 排行柱状图（纵向）
export const rankingBarOption = ({ categories = [], values = [], name = "", horizontal = false } = {}) => {
  const t = themeColors();
  const cat = {
    type: "category",
    data: categories,
    axisLine: { lineStyle: { color: t.split } },
    axisLabel: { color: t.axis, fontSize: 11, interval: 0, rotate: horizontal ? 0 : 20 },
    axisTick: { show: false }
  };
  const val = {
    type: "value",
    splitLine: { lineStyle: { color: t.split } },
    axisLabel: { color: t.axis, fontSize: 11 }
  };
  return {
    color: CHART_COLORS,
    backgroundColor: "transparent",
    tooltip: baseTooltip({ axisPointer: { type: "shadow" } }),
    grid: { ...baseGrid, left: horizontal ? 90 : 12 },
    xAxis: horizontal ? val : cat,
    yAxis: horizontal ? { ...cat, inverse: true } : val,
    series: [
      {
        name,
        type: "bar",
        barMaxWidth: 26,
        itemStyle: { borderRadius: horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0], color: CHART_COLORS[0] },
        data: values
      }
    ]
  };
};

export default { CHART_COLORS, pieOption, trendLineOption, rankingBarOption };
